/**
 * DeepLinkNavigator (R8.2 — Task 4: Cold Start Race Condition).
 *
 * PROBLEM:
 *   When the app is completely closed (cold start) and the user taps a push notification,
 *   `pushNotificationActionPerformed` fires BEFORE React Router and AuthContext have
 *   finished initializing. A direct navigate() call fails or gets overwritten by the
 *   ProtectedRoute redirect to /login.
 *
 * SOLUTION:
 *   1. PushRegistrationBoot stores the target route in pendingDeepLink (module variable).
 *   2. This component is mounted inside BrowserRouter in App.tsx.
 *   3. It watches `isLoading` and `user` from AuthContext.
 *   4. Once auth resolves (isLoading = false):
 *      - If user is authenticated: consume the pending link and navigate immediately.
 *      - If not authenticated: leave the link pending; ProtectedRoute will send the user
 *        to /login first. After successful login, the AppShell mounts, PushRegistrationBoot
 *        mounts, and sets up listeners — but we also check for the pending link in a
 *        separate useEffect that fires when user transitions null → AuthUser.
 *
 * This component renders null and has no visible UI.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { consumePendingDeepLink } from '../platform/pendingDeepLink';
import { useAuth } from '../context/AuthContext';

export function DeepLinkNavigator() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait until auth state is fully resolved
    if (isLoading) return;
    // Only navigate if the user is authenticated (ProtectedRoute ensures /login redirect otherwise)
    if (!user) return;

    const link = consumePendingDeepLink();
    if (link) {
      navigate(link, { replace: false });
    }
    // Re-run whenever auth resolves (covers: cold start, login after tap)
  }, [isLoading, user, navigate]);

  return null;
}
